/**
 * Damped wheel scrolling.
 *
 * Rather than pinning the page in a `position: fixed` wrapper and translating it
 * (the usual "butter"/smooth-scroll trick), this interpolates the document's own
 * scroll position. That matters here: a transformed wrapper becomes the
 * containing block for every `position: fixed` descendant, which would break the
 * floating TOC, the article aside and the camera-roll lightbox — all of which
 * live inside the page content. Animating the real scroll position keeps those
 * fixed, keeps `IntersectionObserver` honest, and keeps every existing scroll
 * listener (progress bar, custom scrollbar, share rail) in step with what the
 * reader actually sees.
 *
 * Only the wheel is intercepted. Touch, keyboard, scrollbar drags, anchor jumps
 * and scroll restoration are left entirely native; the loop notices when one of
 * them moves the page and hands control straight back.
 */


const DAMPER = 0.14;
const SETTLE_PX = 0.5;
const DESYNC_PX = 2;
const LINE_HEIGHT = 16;

const SCROLLABLE = /(auto|scroll|overlay)/;

let enabled = false;
let listening = false;
let supportsInstant = null;
let restoreScrollBehavior = null;

let target = 0;
let current = 0;
let rafId = 0;
let lastTime = 0;
let lastApplied = -1;

const clamp = (value, min, max) => (value < min ? min : value > max ? max : value);

const maxScroll = () =>
  Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;


const detectInstantSupport = () => {
  try {
    document.createElement('div').scrollTo({ top: 0, behavior: 'instant' });
    return true;
  } catch (e) {
    return false;
  }
};

const applyScroll = y => {
  if (supportsInstant) {
    window.scrollTo({ top: y, left: window.scrollX, behavior: 'instant' });
  } else {
    window.scrollTo(window.scrollX, y);
  }
};

export const jumpScrollTo = (top, left = 0) => {
  if (typeof window === 'undefined') return;
  if (supportsInstant === null) supportsInstant = detectInstantSupport();

  if (supportsInstant) {
    window.scrollTo({ top, left, behavior: 'instant' });
    return;
  }

  const root = document.documentElement;
  const previous = root.style.scrollBehavior;
  root.style.scrollBehavior = 'auto';
  window.scrollTo(left, top);
  root.style.scrollBehavior = previous;
};

const normalizeDelta = event => {
  if (event.deltaMode === 1) return event.deltaY * LINE_HEIGHT;
  if (event.deltaMode === 2) return event.deltaY * window.innerHeight;
  return event.deltaY;
};


const hasScrollableAncestor = (node, delta) => {
  const root = document.documentElement;

  while (node && node !== root && node !== document.body) {
    if (node.nodeType === 1) {
      const style = window.getComputedStyle(node);

      if (
        SCROLLABLE.test(style.overflowY) &&
        node.scrollHeight > node.clientHeight + 1
      ) {
        const atTop = node.scrollTop <= 0;
        const atBottom =
          node.scrollTop + node.clientHeight >= node.scrollHeight - 1;
        if (!((delta < 0 && atTop) || (delta > 0 && atBottom))) return true;
      }
    }
    node = node.parentNode || node.host;
  }

  return false;
};

const stop = () => {
  if (rafId) cancelAnimationFrame(rafId);
  rafId = 0;
  lastTime = 0;
  lastApplied = -1;
  target = window.scrollY;
  current = target;
};

const tick = now => {
  const dt = lastTime ? Math.min(now - lastTime, 100) : 1000 / 60;
  lastTime = now;

  if (lastApplied >= 0 && Math.abs(window.scrollY - lastApplied) > DESYNC_PX) {
    stop();
    return;
  }

  const limit = maxScroll();
  if (target > limit) target = limit;

  const diff = target - current;
  if (Math.abs(diff) < SETTLE_PX) {
    applyScroll(target);
    stop();
    return;
  }

  current += diff * (1 - Math.pow(1 - DAMPER, dt / (1000 / 60)));
  applyScroll(current);
  lastApplied = window.scrollY;

  rafId = requestAnimationFrame(tick);
};

const onWheel = event => {
  if (!enabled) return;
  if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) {
    return;
  }
  if (!event.deltaY) return;
  if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
  if (hasScrollableAncestor(event.target, event.deltaY)) return;

  const limit = maxScroll();
  if (limit <= 0) return;

  event.preventDefault();

  if (!rafId) {
    current = window.scrollY;
    target = current;
  }
  target = clamp(target + normalizeDelta(event), 0, limit);

  if (!rafId) {
    lastTime = 0;
    lastApplied = window.scrollY;
    rafId = requestAnimationFrame(tick);
  }
};

const onScroll = () => {
  if (!rafId) {
    target = window.scrollY;
    current = target;
  }
};

const startListening = () => {
  if (listening) return;
  window.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('scroll', onScroll, { passive: true });
  listening = true;
};

const stopListening = () => {
  if (!listening) return;
  window.removeEventListener('wheel', onWheel);
  window.removeEventListener('scroll', onScroll);
  listening = false;
};

export const setSmoothScrollEnabled = next => {
  if (typeof window === 'undefined') return;
  if (next && prefersReducedMotion()) next = false;

  if (next === enabled) {
    if (enabled) stop();
    return;
  }

  enabled = next;

  if (enabled) {
    if (supportsInstant === null) supportsInstant = detectInstantSupport();

    if (!supportsInstant) {
      const root = document.documentElement;
      const previous = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      restoreScrollBehavior = () => {
        root.style.scrollBehavior = previous;
      };
    }

    stop();
    startListening();
    return;
  }

  stopListening();
  stop();

  if (restoreScrollBehavior) {
    restoreScrollBehavior();
    restoreScrollBehavior = null;
  }
};

export default setSmoothScrollEnabled;
